import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';


/**
 * ItemAutocomplete — Input nama barang dengan saran real-time dari katalog.
 *
 * Props:
 *  - value        : string  — nilai saat ini
 *  - onChange     : fn(val) — dipanggil saat user mengetik (tetap bebas input)
 *  - onSelect     : fn(product) — dipanggil saat user memilih dari saran
 *  - placeholder  : string
 *  - style        : object (opsional)
 *  - className    : string (opsional)
 *  - disabled     : bool
 */
export default function ItemAutocomplete({
  value = '',
  onChange,
  onSelect,
  placeholder = 'CARI NAMA BARANG...',
  style = {},
  className = '',
  disabled = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen]               = useState(false);
  const [loading, setLoading]         = useState(false);
  const [activeIdx, setActiveIdx]     = useState(-1);

  const containerRef = useRef(null);
  const debounceRef  = useRef(null);
  const inputRef     = useRef(null);

  // ── Fetch saran dari API ────────────────────────────────────────────────────
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.trim().length === 0) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/products/search`, {
        params: { q: query.trim(), limit: 10 }
      });
      setSuggestions(res.data || []);
      setOpen((res.data || []).length > 0);
    } catch {
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Debounce 300ms ──────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const val = e.target.value;
    if (onChange) onChange(val);
    setActiveIdx(-1);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(val);
    }, 300);
  };

  // ── Pilih saran ─────────────────────────────────────────────────────────────
  const handleSelect = (product) => {
    if (onChange) onChange(product.nama_barang);
    if (onSelect) onSelect(product);
    setSuggestions([]);
    setOpen(false);
    setActiveIdx(-1);
    inputRef.current?.blur();
  };

  // ── Keyboard navigation ─────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && suggestions[activeIdx]) {
        handleSelect(suggestions[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  // ── Tutup dropdown saat klik di luar ────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIdx(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Cleanup debounce ────────────────────────────────────────────────────────
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {/* Input teks */}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          style={{
            textTransform: 'uppercase',
            width: '100%',
            paddingRight: loading ? '2rem' : undefined,
            ...style,
          }}
          className={[
            'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1',
            'text-sm shadow-sm transition-colors placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          ].join(' ')}
        />
        {/* Spinner loading */}
        {loading && (
          <span style={{
            position: 'absolute', right: '0.5rem', top: '50%',
            transform: 'translateY(-50%)', fontSize: '0.7rem', color: '#94a3b8'
          }}>
            ⏳
          </span>
        )}
      </div>

      {/* Dropdown saran */}
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '0.5rem',
          boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
          maxHeight: '280px',
          overflowY: 'auto',
          marginTop: '2px',
        }}>
          {/* Baris saran — nama + satuan saja. Kode barang tetap dibawa di
              payload (lihat onSelect) tapi tidak ditampilkan: dua kolom tidak
              butuh baris header, dan kode hanya menambah beban baca. */}
          {suggestions.map((product, idx) => (
            <div
              key={product.id}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(product); }}
              onMouseEnter={() => setActiveIdx(idx)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: '10px',
                alignItems: 'center',
                backgroundColor: idx === activeIdx ? '#eff6ff' : 'transparent',
                borderBottom: idx < suggestions.length - 1 ? '1px solid #f8fafc' : 'none',
                transition: 'background-color 0.1s',
              }}
            >
              {/* Nama Barang */}
              <span style={{
                fontSize: '0.85rem',
                fontWeight: 500,
                color: '#1e293b',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {product.nama_barang}
              </span>

              {/* Satuan */}
              <span style={{
                fontSize: '0.75rem',
                color: '#64748b',
                backgroundColor: '#f1f5f9',
                padding: '1px 6px',
                borderRadius: '4px',
                textAlign: 'center',
              }}>
                {product.satuan}
              </span>
            </div>
          ))}

          {/* Footer hint */}
          <div style={{
            padding: '5px 12px',
            fontSize: '0.68rem',
            color: '#cbd5e1',
            borderTop: '1px solid #f1f5f9',
            textAlign: 'right',
          }}>
            ↑↓ navigasi · Enter pilih · Esc tutup
          </div>
        </div>
      )}
    </div>
  );
}



